import { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../hooks/useAuth';
import { AuthUser } from '../../services/authService';
import { getLatestAnalysisResult } from '../../services/aiAnalysisService';
import {
  getMyPageData,
  MyPageData,
  selectTitle,
  togglePublicProfile,
} from '../../services/mypageService';
import { AchievementsSection } from './AchievementsSection';
import { AIAnalysisSection } from './AIAnalysisSection';
import './MyPagePanel.css';
import { StatisticsChart } from './StatisticsChart';
import { TitlesSection } from './TitlesSection';

interface MyPagePanelProps {
  visible: boolean;
  onClose: () => void;
  onNavigateToTradingHistory: () => void;
  theme: 'light' | 'dark';
  onGoHome?: () => void;
  highlightPublicProfile?: boolean;
  user: AuthUser | null;
}

// 탭 타입 정의 수정: 'collection' 제거 및 'achievements'로 통합
type TabType = 'overview' | 'statistics' | 'achievements' | 'ai-analysis';

export const MyPagePanel = ({
  visible,
  onClose,
  onNavigateToTradingHistory,
  theme,
  onGoHome,
  highlightPublicProfile,
  user,
}: MyPagePanelProps) => {
  const [data, setData] = useState<MyPageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [aiAnalysisRefreshTrigger, setAiAnalysisRefreshTrigger] = useState(0);
  const [isTogglingProfile, setIsTogglingProfile] = useState(false);
  const [canToggleAgainAt, setCanToggleAgainAt] = useState<Date | null>(null);
  const [canChangeNicknameAt, setCanChangeNicknameAt] = useState<Date | null>(null);
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [editingNickname, setEditingNickname] = useState('');
  const [isUpdatingNickname, setIsUpdatingNickname] = useState(false);
  const [showNicknameErrorPopup, setShowNicknameErrorPopup] = useState(false);
  const [nicknameErrorMessage, setNicknameErrorMessage] = useState('');
  const [showNicknameCooldownPopup, setShowNicknameCooldownPopup] = useState(false);
  const [cooldownDaysRemaining, setCooldownDaysRemaining] = useState(0);
  const balanceRef = useRef<HTMLDivElement>(null);
  const maxBalanceRef = useRef<HTMLDivElement>(null);
  const nicknameInputRef = useRef<HTMLInputElement>(null);
  const { updateNickname } = useAuth();

  useEffect(() => {
    if (visible && !data) {
      loadMyPageData();
    }
  }, [visible]);

  // AI 분석 탭이 활성화될 때마다 최신 결과를 불러오기 위한 트리거 증가
  useEffect(() => {
    if (activeTab === 'ai-analysis') {
      setAiAnalysisRefreshTrigger(prev => prev + 1);
    }
  }, [activeTab]);

  // 로그아웃 시 상태 초기화
  useEffect(() => {
    if (!user) {
      setData(null);
      setLoading(false);
      setError(null);
      setActiveTab('overview');
      setIsTogglingProfile(false);
      setCanToggleAgainAt(null);
      setCanChangeNicknameAt(null);
      setIsEditingNickname(false);
      setEditingNickname('');
      setIsUpdatingNickname(false);
      setShowNicknameErrorPopup(false);
      setNicknameErrorMessage('');
      setShowNicknameCooldownPopup(false);
      setCooldownDaysRemaining(0);
    }
  }, [user]);

  // 자산 값이 넘칠 때 폰트 크기 자동 조절
  useEffect(() => {
    if (!data || activeTab !== 'overview') return;

    const adjustFontSize = (element: HTMLDivElement | null) => {
      if (!element) return;

      const parent = element.parentElement;
      if (!parent) return;

      // 초기 폰트 크기 설정
      const isHighlight = element.classList.contains('highlight');
      const baseSize = isHighlight ? 1.75 : 1.5;
      element.style.fontSize = `${baseSize}rem`;

      // 텍스트가 넘치는지 확인하고 폰트 크기 조절
      const checkOverflow = () => {
        const parentWidth = parent.clientWidth;
        const elementWidth = element.scrollWidth;

        if (elementWidth > parentWidth) {
          // 넘치면 폰트 크기를 줄임
          const ratio = parentWidth / elementWidth;
          const newSize = Math.max(0.7, baseSize * ratio * 0.95); // 최소 0.7rem
          element.style.fontSize = `${newSize}rem`;
        } else {
          // 넘치지 않으면 기본 크기 유지
          element.style.fontSize = `${baseSize}rem`;
        }
      };

      // 약간의 지연 후 확인 (렌더링 완료 후)
      const timeoutId = setTimeout(checkOverflow, 100);

      return timeoutId;
    };

    const timeout1 = adjustFontSize(balanceRef.current);
    const timeout2 = adjustFontSize(maxBalanceRef.current);

    const handleResize = () => {
      adjustFontSize(balanceRef.current);
      adjustFontSize(maxBalanceRef.current);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (timeout1) clearTimeout(timeout1);
      if (timeout2) clearTimeout(timeout2);
      window.removeEventListener('resize', handleResize);
    };
  }, [data, activeTab]);

  const loadMyPageData = async () => {
    setLoading(true);
    setError(null);
    try {
      const myPageData = await getMyPageData(API_BASE_URL);
      setData(myPageData);

      // 제한시간 정보 설정 (안전하게 접근)
      if (myPageData?.user?.canToggleAgainAt) {
        setCanToggleAgainAt(new Date(myPageData.user.canToggleAgainAt));
      } else {
        setCanToggleAgainAt(null);
      }

      // 닉네임 변경 쿨타임 정보 설정
      if (myPageData?.user?.canChangeNicknameAt) {
        setCanChangeNicknameAt(new Date(myPageData.user.canChangeNicknameAt));
      } else {
        setCanChangeNicknameAt(null);
      }

      // AI 분석 결과를 미리 캐시에 저장 (백그라운드에서 처리, 에러 무시)
      if (user?.userKey) {
        getLatestAnalysisResult(API_BASE_URL, user.userKey).catch((err) => {
          console.log('AI 분석 결과 미리 로드 실패 (무시):', err);
        });
      }
    } catch (err: any) {
      console.error('마이페이지 데이터 로드 실패:', err);
      setError(err.message || '마이페이지 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTitle = async (titleId: number) => {
    try {
      await selectTitle(API_BASE_URL, titleId);
      await loadMyPageData(); // 데이터 새로고침
    } catch (err: any) {
      console.error('칭호 선택 실패:', err);
      alert(err.message || '칭호 선택에 실패했습니다.');
    }
  };

  // 닉네임 유효성 검사
  const validateNickname = (nickname: string): { valid: boolean; message?: string } => {
    if (!nickname || nickname.trim().length === 0) {
      return { valid: false, message: '닉네임을 입력해주세요.' };
    }

    if (nickname.length > 10) {
      return { valid: false, message: '닉네임은 최대 10글자까지 입력 가능합니다.' };
    }

    // 한글(완성형, 자음, 모음), 영문, 숫자만 허용
    const validPattern = /^[가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9]+$/;
    if (!validPattern.test(nickname)) {
      return { valid: false, message: '닉네임은 한글, 영문, 숫자만 입력 가능합니다.' };
    }

    return { valid: true };
  };

  const handleStartEditNickname = () => {
    if (!data) return;

    setEditingNickname(data.user.nickname);
    setIsEditingNickname(true);
    // 입력 필드에 포커스
    setTimeout(() => {
      nicknameInputRef.current?.focus();
    }, 100);
  };

  const handleCancelEditNickname = () => {
    setIsEditingNickname(false);
    setEditingNickname('');
  };

  const handleSaveNickname = async () => {
    if (!data) return;

    const trimmedNickname = editingNickname.trim();
    const validation = validateNickname(trimmedNickname);

    if (!validation.valid) {
      setNicknameErrorMessage(validation.message || '닉네임이 올바르지 않습니다.');
      setShowNicknameErrorPopup(true);
      return;
    }

    // 현재 닉네임과 동일하면 변경하지 않음
    if (trimmedNickname === data.user.nickname) {
      setIsEditingNickname(false);
      return;
    }

    // 쿨타임 체크 - 저장 시에 확인
    if (canChangeNicknameAt) {
      const now = new Date();
      const changeDate = new Date(canChangeNicknameAt);
      if (now < changeDate) {
        const daysRemaining = Math.ceil(
          (changeDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );
        setCooldownDaysRemaining(daysRemaining);
        setShowNicknameCooldownPopup(true);
        return;
      }
    }

    setIsUpdatingNickname(true);
    
    // Optimistic Update: 즉시 UI 업데이트
    if (data) {
      setData({
        ...data,
        user: {
          ...data.user,
          nickname: trimmedNickname,
        },
      });
    }
    
    try {
      await updateNickname(trimmedNickname);
      
      // 성공 시 데이터 업데이트 및 쿨타임 정보 갱신
      await loadMyPageData(); // 마이페이지 데이터 새로고침 (쿨타임 정보 포함)
      
      setIsEditingNickname(false);
      setEditingNickname('');
    } catch (err: any) {
      console.error('닉네임 변경 실패:', err);
      
      // 실패 시 이전 상태로 복원
      if (data) {
        setData({
          ...data,
          user: {
            ...data.user,
            nickname: data.user.nickname, // 원래 닉네임으로 복원
          },
        });
      }
      
      // 서버에서 쿨타임 에러가 오는 경우도 팝업으로 표시
      if (err.message && err.message.includes('7일')) {
        const daysMatch = err.message.match(/(\d+)일/);
        if (daysMatch) {
          setCooldownDaysRemaining(parseInt(daysMatch[1], 10));
          setShowNicknameCooldownPopup(true);
        } else {
          alert(err.message || '닉네임 변경에 실패했습니다.');
        }
      } else {
        alert(err.message || '닉네임 변경에 실패했습니다.');
      }
    } finally {
      setIsUpdatingNickname(false);
    }
  };

  const handleTogglePublicProfile = async (isPublic: boolean) => {
    if (isTogglingProfile) return;

    // 제한시간 체크
    if (!isPublic && canToggleAgainAt) {
      const now = new Date();
      const toggleDate = new Date(canToggleAgainAt);
      if (now < toggleDate) {
        const hoursRemaining = Math.ceil(
          (toggleDate.getTime() - now.getTime()) / (1000 * 60 * 60)
        );
        alert(
          `비공개로 전환하려면 최소 1일이 지나야 합니다. (${hoursRemaining}시간 후 가능)`
        );
        return;
      }
    }

    setIsTogglingProfile(true);
    const previousState = data?.user.isPublicProfile;

    // Optimistic update: 즉시 UI 업데이트
    if (data) {
      setData({
        ...data,
        user: {
          ...data.user,
          isPublicProfile: isPublic,
        },
      });
    }

    try {
      const result = await togglePublicProfile(API_BASE_URL, isPublic);

      // 제한시간 정보 저장 (안전하게 처리)
      if (result && result.canToggleAgainAt) {
        setCanToggleAgainAt(new Date(result.canToggleAgainAt));
      } else {
        setCanToggleAgainAt(null);
      }

      // API 응답으로 즉시 상태 업데이트 (새로고침 없이)
      if (data && result) {
        setData({
          ...data,
          user: {
            ...data.user,
            isPublicProfile: result.isPublicProfile,
            canToggleAgainAt: result.canToggleAgainAt,
            lastPublicToggleDate: result.canToggleAgainAt
              ? new Date().toISOString()
              : null,
          },
        });
      }

      // 새로고침은 하지 않음 - API 응답만으로 충분
    } catch (err: any) {
      console.error('프로필 공개 상태 변경 실패:', err);

      // 에러 발생 시 원래 상태로 롤백
      if (data && previousState !== undefined) {
        setData({
          ...data,
          user: {
            ...data.user,
            isPublicProfile: previousState,
          },
        });
      }

      alert(err.message || '프로필 공개 상태 변경에 실패했습니다.');
    } finally {
      setIsTogglingProfile(false);
    }
  };

  if (!visible) {
    return null;
  }

  const formatBalance = (value: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
    }).format(value);
  };

  // 탭 렌더링 헬퍼
  const renderTabContent = () => {
    if (!data) return null;

    switch (activeTab) {
      case 'overview':
        return (
          <div className="tab-content-overview">
            <div className="user-stats-grid">
              <div className="stat-card main-balance">
                <div className="stat-icon">💰</div>
                <div className="stat-info">
                  <div className="stat-label">현재 자산</div>
                  <div
                    ref={balanceRef}
                    className="stat-value highlight auto-scale"
                  >
                    {formatBalance(data.user.balance)}
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📈</div>
                <div className="stat-info">
                  <div className="stat-label">최고 자산</div>
                  <div ref={maxBalanceRef} className="stat-value auto-scale">
                    {formatBalance(data.user.maxBalance)}
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🎮</div>
                <div className="stat-info">
                  <div className="stat-label">플레이 횟수</div>
                  <div className="stat-value">{data.user.gameCount}회</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🏆</div>
                <div className="stat-info">
                  <div className="stat-label">최고 랭킹</div>
                  <div className="stat-value">
                    {data.user.bestRank > 0 ? `${data.user.bestRank}위` : '-'}
                  </div>
                </div>
              </div>
            </div>

            <div className="overview-charts">
              <h3>최근 성과 요약</h3>
              <StatisticsChart statistics={data.statistics} theme={theme} />
            </div>
          </div>
        );
      case 'statistics':
        return (
          <div className="tab-content-statistics">
            <div className="section-header">
              <h3>상세 통계</h3>
              <p>나의 매매 패턴과 성과를 상세하게 분석합니다.</p>
            </div>
            <StatisticsChart statistics={data.statistics} theme={theme} />
          </div>
        );
      case 'achievements':
        return (
          <div className="tab-content-achievements">
            {/* 수집품 섹션 (상단 배치) */}
            <div className="collection-section">
              <div className="section-header">
                <h3>칭호</h3>
                <p>획득한 칭호를 장착하여 프로필을 꾸며보세요.</p>
              </div>

              <div className="collection-group">
                <h4>칭호</h4>
                <TitlesSection
                  titles={data.titles}
                  selectedTitleId={data.selectedTitle?.titleId || null}
                  onSelectTitle={handleSelectTitle}
                />
              </div>
            </div>

            {/* 업적 목록 섹션 (하단 배치) */}
            <div className="achievements-section-container">
              <div className="section-header">
                <h3>업적 목록</h3>
                <p>다양한 업적을 달성하고 새로운 칭호를 획득하세요.</p>
              </div>
              <AchievementsSection achievements={data.achievements} />
            </div>
          </div>
        );
      case 'ai-analysis':
        return (
          <div className="tab-content-ai-analysis">
            {user?.userKey && data?.user ? (
              <AIAnalysisSection
                userKey={user.userKey}
                theme={theme}
                tokenCount={data.user.coinCount ?? 0}
                tradingHistoryCount={data.tradingHistoryCount ?? 0}
                onAnalysisComplete={loadMyPageData}
                refreshTrigger={aiAnalysisRefreshTrigger}
              />
            ) : (
              <div className="ai-analysis-error">
                <span>사용자 정보를 불러올 수 없습니다.</span>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`mypage-page ${theme}`}>
      <div className="mypage-container">
        <div className="mypage-header">
          <div className="mypage-header-left">
            {onGoHome && (
              <button className="mypage-back-btn" onClick={onGoHome}>
                ← 돌아가기
              </button>
            )}
            <h2>마이페이지</h2>
          </div>
        </div>

        <div className="mypage-content-wrapper">
          {loading && (
            <div className="mypage-loading">
              <div className="spinner"></div>
              <p>데이터를 불러오는 중...</p>
            </div>
          )}

          {error && (
            <div className="mypage-error">
              <div className="error-content">
                <p className="error-message">{error}</p>
                {error.includes('접근이 차단되었습니다') && (
                  <div className="error-details">
                    <p className="error-hint">
                      ⚠️ IP가 차단되었습니다. 잠시 후 다시 시도하거나 관리자에게 문의하세요.
                    </p>
                    <p className="error-hint-small">
                      차단은 보통 15분 후 자동으로 해제됩니다.
                    </p>
                  </div>
                )}
              </div>
              <button onClick={loadMyPageData}>다시 시도</button>
            </div>
          )}

          {data && !loading && (
            <div className="mypage-layout">
              {/* 좌측 프로필 사이드바 */}
              <aside className="mypage-sidebar">
                <div className="user-profile-card">
                  <div className="user-avatar-circle">
                    {data.selectedTitle?.icon || '👤'}
                  </div>
                  {isEditingNickname ? (
                    <div className="nickname-edit-container">
                      <input
                        ref={nicknameInputRef}
                        type="text"
                        className="nickname-edit-input"
                        value={editingNickname}
                        onChange={e => setEditingNickname(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            handleSaveNickname();
                          } else if (e.key === 'Escape') {
                            handleCancelEditNickname();
                          }
                        }}
                        maxLength={10}
                        disabled={isUpdatingNickname}
                        placeholder="닉네임 입력"
                      />
                      <div className="nickname-edit-actions">
                        <button
                          className="nickname-save-btn"
                          onClick={handleSaveNickname}
                          disabled={isUpdatingNickname}
                        >
                          {isUpdatingNickname ? '저장 중...' : '저장'}
                        </button>
                        <button
                          className="nickname-cancel-btn"
                          onClick={handleCancelEditNickname}
                          disabled={isUpdatingNickname}
                        >
                          취소
                        </button>
                      </div>
                      <div className="nickname-edit-hint">
                        한글 3개, 영문/숫자 포함 최대 10자
                      </div>
                    </div>
                  ) : (
                    <div className="nickname-display-container">
                      <h3 className="user-name">{data.user.nickname}</h3>
                      <div
                        className="nickname-edit-btn-wrapper"
                        title="닉네임 수정"
                      >
                        <button
                          className="nickname-edit-btn"
                          onClick={handleStartEditNickname}
                        >
                          ✏️
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="user-email">{data.user.email}</p>

                  <div className="user-badges-display">
                    {data.selectedTitle && (
                      <div className="selected-badge title-badge">
                        {data.selectedTitle.icon} {data.selectedTitle.name}
                      </div>
                    )}
                  </div>

                  <div className="sidebar-divider"></div>

                  {/* 프로필 공개 설정 */}
                  <div
                    className={`public-setting ${
                      highlightPublicProfile ? 'highlight-pulse' : ''
                    }`}
                  >
                    <div className="public-setting-header">
                      <div className="public-setting-icon">
                        {data.user?.isPublicProfile ? '🌐' : '🔒'}
                      </div>
                      <div className="public-setting-content">
                        <div className="public-setting-label">
                          <span className="public-setting-title">
                            프로필 공개
                          </span>
                          <label className="public-toggle-switch">
                            <input
                              type="checkbox"
                              checked={data.user?.isPublicProfile || false}
                              onChange={e =>
                                handleTogglePublicProfile(e.target.checked)
                              }
                              disabled={
                                isTogglingProfile ||
                                (!data.user?.isPublicProfile &&
                                  canToggleAgainAt !== null &&
                                  new Date() < new Date(canToggleAgainAt))
                              }
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                        <div className="public-setting-hint">
                          {isTogglingProfile
                            ? '처리 중...'
                            : !data.user?.isPublicProfile &&
                              canToggleAgainAt !== null &&
                              new Date() < new Date(canToggleAgainAt)
                            ? (() => {
                                const hoursRemaining = Math.ceil(
                                  (new Date(canToggleAgainAt).getTime() -
                                    new Date().getTime()) /
                                    (1000 * 60 * 60)
                                );
                                return `비공개 전환 가능까지 ${hoursRemaining}시간 남았어요`;
                              })()
                            : data.user?.isPublicProfile
                            ? '✨ 다른 사용자가 내 매매내역을 볼 수 있어요'
                            : '다른 사용자가 내 매매내역을 볼 수 없어요'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="sidebar-divider"></div>

                  <div className="sidebar-quick-stats">
                    <div className="quick-stat">
                      <span>승률</span>
                      <span
                        className={`value ${
                          data.statistics.winRate >= 50 ? 'positive' : 'neutral'
                        }`}
                      >
                        {data.statistics.winRate.toFixed(1)}%
                      </span>
                    </div>
                    <div className="quick-stat">
                      <span>총 수익</span>
                      <span
                        className={`value ${
                          data.statistics.totalProfit >= 0
                            ? 'positive'
                            : 'negative'
                        }`}
                      >
                        {(data.statistics.totalProfit / 10000).toFixed(0)}만
                      </span>
                    </div>
                  </div>

                  <button
                    className="mypage-action-btn"
                    onClick={onNavigateToTradingHistory}
                  >
                    📊 매매내역 보기
                  </button>
                </div>
              </aside>

              {/* 우측 메인 컨텐츠 */}
              <main className="mypage-main">
                <nav className="mypage-tabs">
                  <button
                    className={`tab-btn ${
                      activeTab === 'overview' ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab('overview')}
                  >
                    개요
                  </button>
                  <button
                    className={`tab-btn ${
                      activeTab === 'statistics' ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab('statistics')}
                  >
                    상세 통계
                  </button>
                  <button
                    className={`tab-btn ${
                      activeTab === 'achievements' ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab('achievements')}
                  >
                    업적 & 칭호
                  </button>
                  <button
                    className={`tab-btn ${
                      activeTab === 'ai-analysis' ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab('ai-analysis')}
                  >
                    🤖 AI 분석
                  </button>
                </nav>

                <div className="tab-content-area">{renderTabContent()}</div>
              </main>
            </div>
          )}
        </div>
      </div>

      {/* 닉네임 검증 오류 팝업 */}
      {showNicknameErrorPopup && (
        <div className="logout-confirm-overlay" onClick={() => setShowNicknameErrorPopup(false)}>
          <div className="logout-confirm-popup" onClick={e => e.stopPropagation()}>
            <div className="logout-icon">⚠️</div>
            <h2>닉네임 입력 오류</h2>
            <p className="logout-message">
              {nicknameErrorMessage}
            </p>
            <div className="logout-buttons">
              <button
                onClick={() => setShowNicknameErrorPopup(false)}
                className="logout-confirm-btn"
                style={{
                  background: 'linear-gradient(135deg, var(--accent-blue), #4a90e2)',
                  boxShadow: '0 4px 15px rgba(74, 144, 226, 0.3)',
                }}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 닉네임 변경 쿨타임 안내 팝업 */}
      {showNicknameCooldownPopup && (
        <div className="logout-confirm-overlay" onClick={() => setShowNicknameCooldownPopup(false)}>
          <div className="logout-confirm-popup" onClick={e => e.stopPropagation()}>
            <div className="logout-icon">⏰</div>
            <h2>닉네임 변경 제한</h2>
            <p className="logout-message">
              닉네임은 7일에 한 번만 변경할 수 있습니다.
              <br />
              <strong>{cooldownDaysRemaining}일 후</strong>에 변경 가능합니다.
            </p>
            <div className="logout-buttons">
              <button
                onClick={() => setShowNicknameCooldownPopup(false)}
                className="logout-confirm-btn"
                style={{
                  background: 'linear-gradient(135deg, var(--accent-blue), #4a90e2)',
                  boxShadow: '0 4px 15px rgba(74, 144, 226, 0.3)',
                }}
              >
                확인
              </button>
              <button
                onClick={() => setShowNicknameCooldownPopup(false)}
                className="logout-cancel-btn"
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
